import * as R from 'ramda';
import {
  ModelIDKeyConditionInput,
  ModelFloatKeyConditionInput,
  ScheduledEventMetadataByTenantLandscapeAndIdQueryVariables,
  ScheduledEventMetadatasByTenantLandscapeQueryVariables,
  ScheduledHolidayMetadataByTenantLandscapeAndIdQuery,
  ScheduledHolidayMetadataByTenantLandscapeAndIdQueryVariables,
} from '@/API';
import {
  PublicHolidaysByLanguageQueryVariables,
  PublicHolidaysByLanguageQuery,
  publicHolidaysByLanguage,
} from '@/appsync/graphql/publicHoliday';
import {
  ScheduledEventHistoriesByTenantLandscapeAndEventStartDateTimeQuery,
  ScheduledEventHistoriesByTenantLandscapeAndEventStartDateTimeQueryVariables,
  ScheduledEventHistory,
} from '@/appsync/graphql/scheduledEventHistory';
import { getScheduledEventHistories } from '@/appsync/graphql/scheduledEventHistory';
import {
  ScheduledEventMetadata,
  ScheduledEventMetadataByTenantLandscapeAndIdQuery,
  ScheduledEventMetadatasByTenantLandscapeQuery,
  scheduledEventMetadataByTenantLandscapeAndIdWithConnection,
  scheduledEventMetadatasByTenantLandscapeWithConnection,
} from '@/appsync/graphql/scheduledEventMetadata';
import {
  ScheduledHolidayMetadata,
  ScheduledHolidayMetadataLabel,
  ScheduledHolidayMetadataLabelsByTenantLandscapeQuery,
  ScheduledHolidayMetadataLabelsByTenantLandscapeQueryVariables,
  ScheduledHolidayMetadatasByTenantLandscapeQuery,
  ScheduledHolidayMetadatasByTenantLandscapeQueryVariables,
} from '@/appsync/graphql/scheduledHolidayMetadata';
import {
  getScheduledHolidayMetadatas,
  scheduledHolidayMetadataLabelsByTenantLandscape,
} from '@/appsync/graphql/scheduledHolidayMetadata';
import { TaskHistory } from '@/appsync/graphql/taskHistory';
import { graphqlQuery } from '@/appsync/service/GraphqlUtils';
import { scheduledHolidayMetadataByTenantLandscapeAndId } from '@/graphql/queries';
import {
  ScheduledEventHistoryForFront,
  ScheduledEventMetadataWithDetailForFront,
  ScheduledHolidayMetadataForFront,
  ScheduledHolidayMetadataWithDetailForFront,
  PublicHolidayForFront,
} from '@/types/type';
import {
  getEndDayOfThisMonth,
  getFirstDayOfThisMonth,
  getSHA3OfTenantLandscape,
  _BooleanEnum2boolean,
} from '@/utils/CommonUtils';
import {
  flattenLabels,
  returnHeadIfNotEmptyArrayElseNull,
} from '@/utils/EventCalendarUtils';
import { newPortalDate, PortalDate } from '@/utils/PortalDate';
import { parseRRule } from '@/utils/RRuleUtils';
import { TriggerOnHolidays } from '@/utils/consts';

const _fetchScheduledEventMetadata = async (
  tenantLandscape: string,
  scheduledEventMetadataId: string
): Promise<ScheduledEventMetadataWithDetailForFront | null> => {
  const scheduledEventMetadataIdVariable: ModelIDKeyConditionInput = {
    eq: scheduledEventMetadataId,
  };
  const variables: ScheduledEventMetadataByTenantLandscapeAndIdQueryVariables = {
    tenantLandscape,
    id: scheduledEventMetadataIdVariable,
  };

  const result = await graphqlQuery<
    ScheduledEventMetadataByTenantLandscapeAndIdQuery,
    ScheduledEventMetadataByTenantLandscapeAndIdQueryVariables
  >(scheduledEventMetadataByTenantLandscapeAndIdWithConnection, variables);
  const scheduledEventMetadataItems:
    | ScheduledEventMetadata[]
    | null
    | undefined = R.path(
    ['scheduledEventMetadataByTenantLandscapeAndId', 'items'],
    result
  );

  const items = eliminateDbDependencyForMetadataWithDetail(
    scheduledEventMetadataItems
  );

  return returnHeadIfNotEmptyArrayElseNull(
    items
  ) as ScheduledEventMetadataWithDetailForFront | null;
};

export const fetchScheduledEventMetadata = async (
  tenant: string,
  landscape: string,
  scheduledEventMetadataId: string
): Promise<ScheduledEventMetadataWithDetailForFront | null> => {
  const tenantLandscape: string = getSHA3OfTenantLandscape(tenant, landscape);
  return _fetchScheduledEventMetadata(
    tenantLandscape,
    scheduledEventMetadataId
  );
};

const _fetchScheduledEventMetadatas = async (
  tenantLandscape: string,
  nextToken: string | null = null
): Promise<ScheduledEventMetadataWithDetailForFront[]> => {
  const scheduledEventMetadatas: ScheduledEventMetadataWithDetailForFront[] = [];
  let isFirstExecution = true;
  while (isFirstExecution || nextToken) {
    isFirstExecution = false;
    const variables: ScheduledEventMetadatasByTenantLandscapeQueryVariables = {
      tenantLandscape,
      nextToken,
    };

    const result = await graphqlQuery<
      ScheduledEventMetadatasByTenantLandscapeQuery,
      ScheduledEventMetadatasByTenantLandscapeQueryVariables
    >(scheduledEventMetadatasByTenantLandscapeWithConnection, variables);
    const scheduledEventMetadataItems:
      | ScheduledEventMetadata[]
      | undefined = R.path(
      ['scheduledEventMetadatasByTenantLandscape', 'items'],
      result
    );
    if (scheduledEventMetadataItems) {
      const items = eliminateDbDependencyForMetadataWithDetail(
        scheduledEventMetadataItems
      );
      scheduledEventMetadatas.push(...items);
    }
    nextToken = R.path(
      ['scheduledEventMetadatasByTenantLandscape', 'nextToken'],
      result
    ) as string | null;
  }
  return scheduledEventMetadatas;
};

export const fetchScheduledEventMetadatas: (
  tenant: string,
  landscape: string
) => Promise<ScheduledEventMetadataWithDetailForFront[]> = R.pipe(
  getSHA3OfTenantLandscape,
  _fetchScheduledEventMetadatas
);

const eliminateDbDependencyForMetadataWithDetail = (
  scheduledEventMetadataItems: ScheduledEventMetadata[] | null | undefined
): ScheduledEventMetadataWithDetailForFront[] => {
  if (!scheduledEventMetadataItems) {
    return [];
  }

  const scheduledEventMetadataWithDetailForFront: ScheduledEventMetadataWithDetailForFront[] = [];

  scheduledEventMetadataItems.forEach(scheduledEventMetadataItem => {
    const job = R.prop('job', scheduledEventMetadataItem);
    const rrule = R.prop('rruleSet', scheduledEventMetadataItem);
    scheduledEventMetadataWithDetailForFront.push({
      ...R.pick(
        [
          'id',
          'title',
          'description',
          'repeatType',
          'rruleSet',
          'holidayExecutionType',
          'customHolidayLabels',
          'updatedBy',
          'editedAt',
          'eventTriggerType',
        ],
        scheduledEventMetadataItem
      ),
      ...parseRRule(rrule),
      job: {
        hasOutputFile: _BooleanEnum2boolean(R.prop('hasOutputFile', job)),
        ...R.pick(['id', 'title'], job),
      },
      triggerOnHolidays: _BooleanEnum2boolean(
        R.prop('useCustomHoliday', scheduledEventMetadataItem)
      )
        ? TriggerOnHolidays.customHolidays
        : TriggerOnHolidays.allHolidays,
    });
  });
  return scheduledEventMetadataWithDetailForFront;
};

const _fetchScheduledHolidayMetadatas = async (
  tenantLandscape: string,
  nextToken: string | null = null
): Promise<ScheduledHolidayMetadataForFront[]> => {
  const scheduledHolidayMetadatas: ScheduledHolidayMetadataForFront[] = [];
  let isFirstExecution = true;
  while (isFirstExecution || nextToken) {
    isFirstExecution = false;
    const variables: ScheduledHolidayMetadatasByTenantLandscapeQueryVariables = {
      tenantLandscape,
      nextToken,
    };

    const result = await graphqlQuery<
      ScheduledHolidayMetadatasByTenantLandscapeQuery,
      ScheduledHolidayMetadatasByTenantLandscapeQueryVariables
    >(getScheduledHolidayMetadatas, variables);
    const scheduledHolidayItems = R.path(
      ['scheduledHolidayMetadatas', 'items'],
      result
    );
    if (scheduledHolidayItems) {
      scheduledHolidayMetadatas.push(
        ...(scheduledHolidayItems as ScheduledHolidayMetadata[])
      );
    }
    nextToken = R.path(['scheduledHolidayMetadatas', 'nextToken'], result) as
      | string
      | null;
  }
  return scheduledHolidayMetadatas;
};

export const fetchScheduledHolidayMetadatas: (
  tenant: string,
  landscape: string
) => Promise<ScheduledHolidayMetadataForFront[]> = R.pipe(
  getSHA3OfTenantLandscape,
  _fetchScheduledHolidayMetadatas
);

const _fetchScheduledHolidayMetadata = async (
  tenantLandscape: string,
  scheduledHolidayMetadataId: string
): Promise<ScheduledHolidayMetadataWithDetailForFront | null> => {
  const scheduledHolidayMetadataIdVariable: ModelIDKeyConditionInput = {
    eq: scheduledHolidayMetadataId,
  };
  const variables: ScheduledHolidayMetadataByTenantLandscapeAndIdQueryVariables = {
    tenantLandscape,
    id: scheduledHolidayMetadataIdVariable,
  };

  const result = await graphqlQuery<
    ScheduledHolidayMetadataByTenantLandscapeAndIdQuery,
    ScheduledHolidayMetadataByTenantLandscapeAndIdQueryVariables
  >(scheduledHolidayMetadataByTenantLandscapeAndId, variables);
  const scheduledHolidayMetadataItems:
    | ScheduledHolidayMetadata[]
    | null
    | undefined = R.path(
    ['scheduledHolidayMetadataByTenantLandscapeAndId', 'items'],
    result
  );
  const items = eliminateDbDependencyForHolidayMetadataWithDetail(
    scheduledHolidayMetadataItems
  );

  return returnHeadIfNotEmptyArrayElseNull(
    items
  ) as ScheduledHolidayMetadataWithDetailForFront | null;
};

export const fetchScheduledHolidayMetadata = async (
  tenant: string,
  landscape: string,
  scheduledHolidayMetadataId: string
): Promise<ScheduledHolidayMetadataWithDetailForFront | null> => {
  const tenantLandscape: string = getSHA3OfTenantLandscape(tenant, landscape);
  return _fetchScheduledHolidayMetadata(
    tenantLandscape,
    scheduledHolidayMetadataId
  );
};

const eliminateDbDependencyForHolidayMetadataWithDetail = (
  scheduledHolidayMetadataItems: ScheduledHolidayMetadata[] | null | undefined
): ScheduledHolidayMetadataWithDetailForFront[] => {
  if (!scheduledHolidayMetadataItems) {
    return [];
  }
  const scheduledHolidayMetadataWithDetailForFront: ScheduledHolidayMetadataWithDetailForFront[] = [];

  scheduledHolidayMetadataItems.forEach(scheduledHolidayMetadataItem => {
    const rrule = R.prop('rruleSet', scheduledHolidayMetadataItem);
    scheduledHolidayMetadataWithDetailForFront.push({
      ...R.pick(
        [
          'id',
          'title',
          'description',
          'repeatType',
          'rruleSet',
          'labels',
          'updatedBy',
          'updatedAt',
        ],
        scheduledHolidayMetadataItem
      ),
      ...parseRRule(rrule),
    });
  });
  return scheduledHolidayMetadataWithDetailForFront;
};

export const fetchPublicHolidays = async (
  language: string,
  nextToken: string | null = null
): Promise<PublicHolidayForFront[]> => {
  const publicHolidays: PublicHolidayForFront[] = [];
  let isFirstExecution = true;
  while (isFirstExecution || nextToken) {
    isFirstExecution = false;
    const variables: PublicHolidaysByLanguageQueryVariables = {
      language,
      nextToken,
    };

    const result = await graphqlQuery<
      PublicHolidaysByLanguageQuery,
      PublicHolidaysByLanguageQueryVariables
    >(publicHolidaysByLanguage, variables);
    const publicHolidayItems = R.path(['publicHolidays', 'items'], result);
    if (publicHolidayItems) {
      publicHolidays.push(...(publicHolidayItems as PublicHolidayForFront[]));
    }
    nextToken = R.path(['publicHolidays', 'nextToken'], result) as
      | string
      | null;
  }
  return publicHolidays;
};

const _fetchScheduledHolidayMetadataLabels = async (
  tenantLandscape: string,
  nextToken: string | undefined | null = null
): Promise<string[]> => {
  const scheduledHolidayMetadataLabels: ScheduledHolidayMetadataLabel[] = [];
  let isFirstExecution = true;
  while (isFirstExecution || nextToken) {
    isFirstExecution = false;
    const variables: ScheduledHolidayMetadataLabelsByTenantLandscapeQueryVariables = {
      tenantLandscape,
      nextToken: nextToken ? nextToken : null,
    };

    const result = await graphqlQuery<
      ScheduledHolidayMetadataLabelsByTenantLandscapeQuery,
      ScheduledHolidayMetadataLabelsByTenantLandscapeQueryVariables
    >(scheduledHolidayMetadataLabelsByTenantLandscape, variables);
    const scheduledHolidayItems = R.path<ScheduledHolidayMetadataLabel[]>(
      ['scheduledHolidayMetadatasByTenantLandscape', 'items'],
      result
    );
    if (scheduledHolidayItems) {
      scheduledHolidayMetadataLabels.push(...scheduledHolidayItems);
    }
    nextToken = R.path<string | null>(
      ['scheduledHolidayMetadatasByTenantLandscape', 'nextToken'],
      result
    );
  }
  return R.pipe(flattenLabels, R.uniq)(scheduledHolidayMetadataLabels);
};

export const fetchScheduledHolidayMetadataLabels: (
  tenant: string,
  landscape: string
) => Promise<string[]> = R.pipe(
  getSHA3OfTenantLandscape,
  _fetchScheduledHolidayMetadataLabels
);

export const fetchScheduledEventHistoryByFocusDateAndMetadataId = async (
  tenant: string,
  landscape: string,
  focusDate: PortalDate,
  eventMetadataId: string
): Promise<ScheduledEventHistoryForFront | null> => {
  const tenantLandscape: string = getSHA3OfTenantLandscape(tenant, landscape);
  const eventHistories = await _fetchScheduledEventHistoriesBasedOnBetweenDates(
    tenantLandscape,
    [focusDate, focusDate.add(1, 'day').subtract(1, 'second')]
  );
  return (
    eventHistories.find(
      ({ scheduledEventMetadataId }) =>
        eventMetadataId === scheduledEventMetadataId
    ) || null
  );
};

export const fetchScheduledEventHistoriesForTargetMonth = async (
  tenant: string,
  landscape: string,
  focusDate: string
): Promise<ScheduledEventHistoryForFront[]> => {
  const focusPortalDate = newPortalDate(focusDate, 'YYYY-MM-DD');
  const scheduledEventHistories = await _fetchScheduledEventHistoriesBasedOnBetweenDates(
    getSHA3OfTenantLandscape(tenant, landscape),
    [
      getFirstDayOfThisMonth(focusPortalDate),
      getEndDayOfThisMonth(focusPortalDate),
    ]
  );
  return scheduledEventHistories;
};

export const fetchScheduledEventHistoriesBasedOnBetweenDates = async (
  tenant: string,
  landscape: string,
  startDateTime: PortalDate,
  endDateTime: PortalDate
): Promise<ScheduledEventHistoryForFront[]> => {
  const scheduledEventHistories = await _fetchScheduledEventHistoriesBasedOnBetweenDates(
    getSHA3OfTenantLandscape(tenant, landscape),
    [startDateTime, endDateTime]
  );
  return scheduledEventHistories;
};

const _fetchScheduledEventHistoriesBasedOnBetweenDates = async (
  tenantLandscape: string,
  between: PortalDate[],
  nextToken: string | null = null
): Promise<ScheduledEventHistoryForFront[]> => {
  const scheduledEventHistories: ScheduledEventHistoryForFront[] = [];
  let isFirstExecution = true;

  const [start, end] = between;
  const eventStartDateTime: ModelFloatKeyConditionInput = {
    between: [start.toEpochSeconds(), end.toEpochSeconds()],
  };

  while (isFirstExecution || nextToken) {
    isFirstExecution = false;
    const variables: ScheduledEventHistoriesByTenantLandscapeAndEventStartDateTimeQueryVariables = {
      tenantLandscape,
      eventStartDateTime,
      nextToken,
    };

    const result = await graphqlQuery<
      ScheduledEventHistoriesByTenantLandscapeAndEventStartDateTimeQuery,
      ScheduledEventHistoriesByTenantLandscapeAndEventStartDateTimeQueryVariables
    >(getScheduledEventHistories, variables);
    const scheduledEventHistoryItems = R.path(
      ['scheduledEventHistories', 'items'],
      result
    );
    if (scheduledEventHistoryItems) {
      const items = generateScheduledEventHistoryForFront(
        scheduledEventHistoryItems as ScheduledEventHistory[]
      );
      scheduledEventHistories.push(...items);
    }
    nextToken = R.path(['scheduledEventHistories', 'nextToken'], result) as
      | string
      | null;
  }
  return scheduledEventHistories;
};

const generateScheduledEventHistoryForFront = (
  scheduledEventHistoryItems: ScheduledEventHistory[]
): ScheduledEventHistoryForFront[] => {
  const scheduledEventHistoriesForFront: ScheduledEventHistoryForFront[] = [];

  scheduledEventHistoryItems.forEach(scheduledEventHistoryItem => {
    const taskHistories: TaskHistory[] = [];

    const taskHistoryItems: TaskHistory[] | undefined = R.path(
      ['taskHistories', 'items'],
      scheduledEventHistoryItem
    );
    if (taskHistoryItems) {
      taskHistoryItems.sort((x: TaskHistory, y: TaskHistory) => {
        return x.order - y.order;
      });
      taskHistories.push(...taskHistoryItems);
    }
    const job = R.prop('job', scheduledEventHistoryItem);

    scheduledEventHistoriesForFront.push({
      ...R.pick(
        [
          'id',
          'title',
          'description',
          'scheduledEventMetadataId',
          'eventStartDateTime',
          'jobExecutionStatus',
          'jobId',
          'executedBy',
          'updatedAt',
          'includeInDownload',
        ],
        scheduledEventHistoryItem
      ),
      job: {
        hasOutputFile: _BooleanEnum2boolean(R.prop('hasOutputFile', job)),
        ...R.pick(['id', 'title'], job),
      },
      taskHistories: {
        items: taskHistories,
      },
    });
  });
  return scheduledEventHistoriesForFront;
};
